import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { MainLayoutComponent } from '../../../_layouts/main-layout/main-layout.component';
import { AlertService } from '../../../_services/alert.service';
import { ProductService, ProductPickerItem } from '../../../_services/product.service';
import { UserService } from '../../../_services/users.service';
import { SupplierService } from '../../../_services/supplier.service';
import { PurchaseOrderService, PurchaseOrderInput } from '../../../_services/purchase-order.service';
import { PurchaseOrderPdfService } from '../../../_services/purchase-order-pdf.service';
import { PurchaseOrderItem } from '../../../_interfaces/purchase-order';
import { purchaseOrderStatus, purchaseOrderBillingStatus } from '../../../../Shared/_constants/constants';
import { CURRENCY_OPTIONS, currencySymbol } from '../currencies';
import { purchaseStatusClass, billingStatusClass } from '../status-styles';
import { AllocationMethod, computeLandedCosts } from '../landed-cost';
import { PORTS_OF_DISCHARGE, PORTS_OF_LOADING } from '../ports';
import { lastListQuery } from '../list-state';
import { Supplier } from '../../../_interfaces/supplier';
import { ProductPickerComponent } from '../../../../Shared/_components/product-picker/product-picker.component';
import { QuickSupplierDialogComponent } from '../../suppliers/quick-supplier-dialog/quick-supplier-dialog.component';

// Backed by PurchaseOrderService (/purchase-orders/*) and SupplierService
// (/suppliers/*) - see nustone-backend's purchase-orders.service.ts.
@Component({
    selector: 'app-create-purchase',
    imports: [
        MainLayoutComponent,
        CommonModule,
        FormsModule,
        RouterLink,
        MatIconModule,
        MatTooltipModule,
        MatMenuModule,
        MatProgressSpinnerModule,
        MatAutocompleteModule,
        ProductPickerComponent,
    ],
    templateUrl: './create-purchase.component.html',
    // Form styles shared with the shipment page.
    styleUrl: '../purchase-form.css'
})
export class CreatePurchaseComponent implements OnInit {
  isEditMode = false;
  purchaseOrderId: number | null = null;
  loading = false;
  saving = false;

  statusOptions = Object.values(purchaseOrderStatus);
  billingStatusOptions = Object.values(purchaseOrderBillingStatus);
  storeOptions = ['Kettering', 'Markstey'];
  // The code is what's stored on the PO; the name is only for picking.
  incotermsOptions = [
    { code: 'EXW', name: 'Ex Works' },
    { code: 'FCA', name: 'Free Carrier' },
    { code: 'FAS', name: 'Free Alongside Ship' },
    { code: 'FOB', name: 'Free On Board' },
    { code: 'CFR', name: 'Cost and Freight' },
    { code: 'CIF', name: 'Cost, Insurance and Freight' },
    { code: 'CPT', name: 'Carriage Paid To' },
    { code: 'CIP', name: 'Carriage and Insurance Paid To' },
    { code: 'DPU', name: 'Delivered at Place Unloaded' },
    { code: 'DAP', name: 'Delivered at Place' },
    { code: 'DDP', name: 'Delivered Duty Paid' },
  ];
  currencyOptions = CURRENCY_OPTIONS;
  currencySymbol = currencySymbol;
  getStatusClass = purchaseStatusClass;
  getBillingStatusClass = billingStatusClass;
  products: ProductPickerItem[] = [];
  suppliers: Supplier[] = [];

  // Blank until the PO is actually saved - the backend assigns it from the
  // new row's own id, so it genuinely doesn't exist before that.
  reference = '';
  supplierId: number | null = null;
  supplierSearchTerm: string = '';
  @ViewChild('supplierInput') supplierInput?: ElementRef<HTMLInputElement>;
  buyerName = '';
  store = 'Markstey';
  currency = 'GBP';
  sourceDocument = '';
  incoterms = '';
  paymentTerms = '';

  // ---- Landed cost ----
  charges: { chargeType: string; description: string; amount: number | null }[] = [];
  allocationMethod: AllocationMethod = 'VALUE';
  exchangeRate = 1;
  landedCostAppliedAt: string | null = null;
  readonly chargeTypeOptions = [
    { value: 'FREIGHT', label: 'Freight' },
    { value: 'INSURANCE', label: 'Insurance' },
    { value: 'DUTY', label: 'Import duty' },
    { value: 'CLEARING', label: 'Clearing / agent fees' },
    { value: 'PORT', label: 'Port / handling' },
    { value: 'OTHER', label: 'Other' },
  ];
  readonly allocationMethods: { value: AllocationMethod; label: string; hint: string }[] = [
    { value: 'VALUE', label: 'By value', hint: 'Higher-value lines carry more of the charges' },
    { value: 'QUANTITY', label: 'By quantity', hint: 'Every unit carries the same share' },
    { value: 'WEIGHT', label: 'By weight', hint: 'Heavier lines carry more of the charges' },
  ];

  addCharge(): void {
    this.charges.push({ chargeType: 'FREIGHT', description: '', amount: null });
  }

  removeCharge(index: number): void {
    this.charges.splice(index, 1);
  }

  get totalCharges(): number {
    return this.charges.reduce((sum, c) => sum + (Number(c.amount) || 0), 0);
  }

  // Live preview of what each line lands at - the backend recalculates the
  // stored figures on save with the same rule.
  get landedPreview() {
    const result = computeLandedCosts(
      this.items.map((i) => ({ quantity: i.quantity, unitCost: i.unitCost, unitWeight: i.unitWeight })),
      this.totalCharges,
      this.allocationMethod,
      this.exchangeRate,
    );
    return { rows: result, fellBack: !!result.fellBack };
  }

  readonly paymentTermsOptions = [
    'CAD',
    '30% On B/L & 70% 90 Days From B/L',
    'Immediate Payment',
    '7 Days',
    'B/L 120 Days',
    'B/L 90 Days',
    'B/L 60 Days',
    '30 Days',
    '14 Days',
    '15 Days',
    '21 Days',
    '45 Days',
    '2 Months',
    'End of Following Month',
    '30 days End of Month on the 10th',
    '30% Now, Balance 60 Days',
    '2/7 Net 30',
  ];

  // Purchase orders saved when this was a free-text box may hold terms that
  // aren't in the list - keep that value selectable so opening and saving
  // such a PO doesn't silently blank it.
  get paymentTermsChoices(): string[] {
    return this.paymentTerms && !this.paymentTermsOptions.includes(this.paymentTerms)
      ? [...this.paymentTermsOptions, this.paymentTerms]
      : this.paymentTermsOptions;
  }
  portOfLoading = '';

  // "Mundra" and "MUNDRA" both existed in the source list - one entry here.
  readonly portOfLoadingOptions = PORTS_OF_LOADING;

  // Keeps a saved port that isn't in the list (typed by hand before this was
  // a dropdown) selectable, so opening and saving that PO doesn't blank it.
  get portOfLoadingChoices(): string[] {
    return this.portOfLoading && !this.portOfLoadingOptions.includes(this.portOfLoading)
      ? [...this.portOfLoadingOptions, this.portOfLoading]
      : this.portOfLoadingOptions;
  }
  portOfDischarge = '';

  readonly portOfDischargeOptions = PORTS_OF_DISCHARGE;

  // Keeps a saved port that isn't in the list (typed by hand before this was
  // a dropdown) selectable, so opening and saving that PO doesn't blank it.
  get portOfDischargeChoices(): string[] {
    return this.portOfDischarge && !this.portOfDischargeOptions.includes(this.portOfDischarge)
      ? [...this.portOfDischargeOptions, this.portOfDischarge]
      : this.portOfDischargeOptions;
  }
  orderDate = new Date().toISOString().slice(0, 10);
  expectedDeliveryDate = '';
  status: string = purchaseOrderStatus.DRAFT;
  billingStatus: string = purchaseOrderBillingStatus.UNBILLED;
  notes = '';
  items: PurchaseOrderItem[] = [];

  // Only used for the previous/next buttons - the full id list in default
  // (orderDate DESC) order, fetched once. Doesn't reflect whatever filter
  // was active on the list page the user came from, same caveat as before.
  private allPurchaseOrderIds: number[] = [];
  private allPurchaseOrderIdsRequested = false;

  private nextItemId = 1;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private alertService: AlertService,
    private productService: ProductService,
    private userService: UserService,
    private supplierService: SupplierService,
    private purchaseOrderService: PurchaseOrderService,
    private purchaseOrderPdfService: PurchaseOrderPdfService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.loadProducts();
    this.loadSuppliers();

    // Angular reuses this component instance across /purchases/edit/:id
    // navigations (same route, different param) rather than recreating it,
    // so the previous/next buttons need a live subscription - ngOnInit
    // alone would only fire once, on the very first id.
    this.route.paramMap.subscribe((params) => {
      const idParam = params.get('id');
      if (idParam) {
        this.isEditMode = true;
        // Only needed for the previous/next buttons, which only show when
        // editing - so a new PO doesn't wait on it. Fetched once.
        if (!this.allPurchaseOrderIdsRequested) {
          this.allPurchaseOrderIdsRequested = true;
          this.loadAllPurchaseOrderIds();
        }
        this.purchaseOrderId = Number(idParam);
        this.loadPurchaseOrder(this.purchaseOrderId);
      } else {
        this.isEditMode = false;
        this.purchaseOrderId = null;
        this.resetForNewPurchaseOrder();
      }
    });
  }

  private resetForNewPurchaseOrder(): void {
    this.reference = '';
    // The buyer is whoever is creating the PO - recorded automatically
    // rather than typed, same reasoning as an order note's salesmanId.
    this.buyerName = this.userService.isLoggedIn().userName || '';
    // ?supplierId= comes from the supplier page ("New purchase order", or
    // straight after adding a supplier from here) - start with them picked.
    const presetSupplier = Number(this.route.snapshot.queryParamMap.get('supplierId'));
    this.supplierId = presetSupplier > 0 ? presetSupplier : null;
    this.supplierSearchTerm = this.supplierId ? this.getSelectedSupplier()?.name || '' : '';
    this.store = 'Markstey';
    this.currency = 'GBP';
    this.sourceDocument = '';
    this.incoterms = '';
    this.paymentTerms = '';
    this.charges = [];
    this.allocationMethod = 'VALUE';
    this.exchangeRate = 1;
    this.landedCostAppliedAt = null;
    this.portOfLoading = '';
    this.portOfDischarge = '';
    this.orderDate = new Date().toISOString().slice(0, 10);
    this.expectedDeliveryDate = '';
    this.status = purchaseOrderStatus.DRAFT;
    this.billingStatus = purchaseOrderBillingStatus.UNBILLED;
    this.notes = '';
    this.items = [];
    this.nextItemId = 1;
    this.addItem();
  }

  private loadProducts(): void {
    this.productService.getProductPicker().subscribe({
      next: (products) => (this.products = products),
      error: () => {
        // Non-fatal for this page: search just stops returning matches
        // and items can still be entered as free text.
        this.products = [];
      },
    });
  }

  private loadSuppliers(): void {
    this.supplierService.getSuppliers().subscribe({
      next: (suppliers) => {
        this.suppliers = suppliers;
        // If a PO was already loaded (edit mode) before this resolved,
        // back-fill the display name now that we actually have it.
        if (this.supplierId && !this.supplierSearchTerm) {
          this.supplierSearchTerm = this.getSelectedSupplier()?.name || '';
        }
      },
      // Non-fatal: the supplier picker just stays empty.
      error: () => (this.suppliers = []),
    });
  }

  // Typing invalidates the previous selection, same reasoning as
  // onProductNameInput - editing the text after picking a supplier
  // shouldn't silently keep pointing at the old one.
  onSupplierSearchInput(): void {
    this.supplierId = null;
  }

  getFilteredSuppliers(query: string): Supplier[] {
    const terms = (query || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
    const matches = terms.length
      ? this.suppliers.filter((s) => {
          const haystack = [s.name, s.phone, s.email, s.city, s.country].join(' ').toLowerCase();
          return terms.every((t) => haystack.includes(t));
        })
      : this.suppliers;
    return matches.slice(0, 20);
  }

  // Splits text into plain / matched pieces so the typed words stand out.
  highlight(text: string | null | undefined, query: string): { text: string; match: boolean }[] {
    const value = String(text ?? '');
    const terms = (query || '').toLowerCase().split(/\s+/).filter(Boolean);
    if (!terms.length || !value) return [{ text: value, match: false }];
    const re = new RegExp(`(${terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'ig');
    return value
      .split(re)
      .filter((part) => part !== '')
      .map((part) => ({ text: part, match: terms.includes(part.toLowerCase()) }));
  }

  supplierMeta(s: Supplier): string {
    return [s.email, s.phone, s.country].filter(Boolean).join(' · ');
  }

  // The autocomplete writes this into the box after a pick.
  supplierDisplay = (value: Supplier | string | null): string =>
    typeof value === 'string' ? value : value?.name ?? '';

  onSupplierPicked(value: Supplier | 'NEW'): void {
    if (value === 'NEW') {
      const typed = this.supplierSearchTerm;
      this.supplierSearchTerm = '';
      this.onNewSupplier(typed);
      return;
    }
    this.supplierId = value.id;
    this.supplierSearchTerm = value.name;
  }

  // Swaps the supplier card back for the search box, ready to type.
  changeSupplier(): void {
    this.clearSupplierSelection();
    setTimeout(() => this.supplierInput?.nativeElement.focus());
  }

  clearSupplierSelection(): void {
    this.supplierId = null;
    this.supplierSearchTerm = '';
  }

  private loadAllPurchaseOrderIds(): void {
    this.purchaseOrderService.getPurchaseOrders({ pageIndex: 0, pageSize: 1000 }).subscribe({
      next: (response) => (this.allPurchaseOrderIds = response.items.map((po) => po.id)),
      // Non-fatal: previous/next just stay disabled.
      error: () => (this.allPurchaseOrderIds = []),
    });
  }

  // What was stored when this PO was loaded, so the form can show how many
  // units the next save will add to (or take off) the store's stock.
  private savedReceivedByItemId = new Map<number, number>();
  private savedStore = '';

  // Stock change the next save will make for this line: the difference in
  // received units against what's already been counted into stock.
  pendingStockChange(item: PurchaseOrderItem): number {
    const received = Number(item.quantityReceived || 0);
    const saved = this.store === this.savedStore ? (this.savedReceivedByItemId.get(item.id) ?? 0) : 0;
    return received - saved;
  }

  // Picking "Received" means everything ordered has arrived - fill the
  // received column so the stock (and status) follow from it.
  onStatusChange(newStatus: string): void {
    if (newStatus === 'RECEIVED') {
      this.items.forEach((item) => {
        if ((item.quantityReceived || 0) < item.quantity) item.quantityReceived = item.quantity;
      });
    }
  }

  private loadPurchaseOrder(id: number): void {
    this.loading = true;
    this.purchaseOrderService.getPurchaseOrderById(id).subscribe({
      next: (po) => {
        this.reference = po.reference;
        this.supplierId = po.supplierId;
        this.supplierSearchTerm = po.supplier?.name || '';
        this.buyerName = po.buyerName;
        this.store = po.store;
        this.currency = po.currency;
        this.sourceDocument = po.sourceDocument || '';
        this.incoterms = po.incoterms || '';
        this.paymentTerms = po.paymentTerms || '';
        this.allocationMethod = (po.allocationMethod as AllocationMethod) || 'VALUE';
        this.exchangeRate = Number(po.exchangeRate) > 0 ? Number(po.exchangeRate) : 1;
        this.landedCostAppliedAt = (po.landedCostAppliedAt as string | null) ?? null;
        this.charges = (po.charges ?? []).map((c) => ({
          chargeType: c.chargeType,
          description: c.description || '',
          amount: Number(c.amount),
        }));
        // Match a saved port to the list ignoring case ("Mundra" -> "MUNDRA").
        this.portOfLoading =
          this.portOfLoadingOptions.find((o) => o.toLowerCase() === (po.portOfLoading || '').trim().toLowerCase()) ||
          po.portOfLoading ||
          '';
        this.portOfDischarge =
          this.portOfDischargeOptions.find((o) => o.toLowerCase() === (po.portOfDischarge || '').trim().toLowerCase()) ||
          po.portOfDischarge ||
          '';
        this.orderDate = this.toDateInputValue(po.orderDate);
        this.expectedDeliveryDate = po.expectedDeliveryDate ? this.toDateInputValue(po.expectedDeliveryDate) : '';
        this.status = po.status;
        this.billingStatus = po.billingStatus;
        this.notes = po.notes || '';
        this.items = po.items.map((item) => ({ ...item }));
        this.savedStore = po.store;
        this.savedReceivedByItemId = new Map(po.items.map((i) => [i.id, Number(i.quantityReceived || 0)]));
        this.nextItemId = (Math.max(0, ...this.items.map((i) => i.id)) || 0) + 1;
        if (this.items.length === 0) this.addItem();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.alertService.alert('Purchase order not found', 'error');
        this.backToList();
      },
    });
  }

  private toDateInputValue(value: Date | string): string {
    return new Date(value).toISOString().slice(0, 10);
  }

  addItem(): void {
    this.items.push({
      id: this.nextItemId++,
      productId: null,
      productName: '',
      sku: '',
      quantity: 1,
      quantityReceived: 0,
      quantityBilled: 0,
      unitCost: 0,
      unitWeight: 0,
      taxRate: 20,
    });
  }

  removeItem(item: PurchaseOrderItem): void {
    this.items = this.items.filter((i) => i.id !== item.id);
  }

  // Typing always sets the free-text productName directly (see the input's
  // ngModel binding) - this only invalidates a stale productId once the
  // text no longer matches what was picked, so editing after a selection
  // doesn't silently keep pointing at the wrong product.
  onProductNameInput(item: PurchaseOrderItem): void {
    item.productId = null;
  }

  selectProduct(item: PurchaseOrderItem, product: ProductPickerItem): void {
    item.productId = product.productId;
    item.productName = product.productName;
    item.sku = product.sku || item.sku;
    item.unitWeight = product.weight ?? 0;
    // Tax follows the product's own Vendor Tax setting (General Information
    // on the product edit page) rather than staying manually typed - ZERO_RATE
    // is 0%, STANDARD_RATE is 20%. Left untouched if the product has neither
    // set, so a manual entry isn't silently overwritten with a guess.
    if (product.vendorTax === 'ZERO_RATE') {
      item.taxRate = 0;
    } else if (product.vendorTax === 'STANDARD_RATE') {
      item.taxRate = 20;
    }
  }

  // On hand = the product's total stock across both yards. Resolved from
  // the same picker list used to search for the product - it already
  // settles on one variant per product (see getProductPicker), so this
  // reuses that rather than guessing among several variants itself.
  getOnHandQuantity(item: PurchaseOrderItem): number | string {
    if (!item.productId) return '-';
    const product = this.products.find((p) => p.productId === item.productId);
    if (!product) return '-';
    return (product.kStockQty ?? 0) + (product.mStockQty ?? 0);
  }

  // Stock in both yards for the line's product - the On Hand number is only
  // the PO's own store, so units sitting in the other yard would otherwise
  // look like there's no stock at all.
  getStockBreakdown(item: PurchaseOrderItem): { k: number; m: number; total: number } | null {
    if (!item.productId) return null;
    const product = this.products.find((p) => p.productId === item.productId);
    if (!product) return null;
    const k = product.kStockQty ?? 0;
    const m = product.mStockQty ?? 0;
    return { k, m, total: k + m };
  }

  // Pre-tax line value.
  getLineSubtotal(item: PurchaseOrderItem): number {
    return (item.quantity || 0) * (item.unitCost || 0);
  }

  getLineTax(item: PurchaseOrderItem): number {
    return this.getLineSubtotal(item) * ((item.taxRate || 0) / 100);
  }

  getSubtotal(): number {
    return this.items.reduce((sum, item) => sum + this.getLineSubtotal(item), 0);
  }

  getTotalTax(): number {
    return this.items.reduce((sum, item) => sum + this.getLineTax(item), 0);
  }

  getGrandTotal(): number {
    return this.getSubtotal() + this.getTotalTax();
  }

  // ---- Progress ----
  get totalOrdered(): number {
    return this.items.reduce((n, i) => n + (Number(i.quantity) || 0), 0);
  }

  get totalReceived(): number {
    return this.items.reduce((n, i) => n + (Number(i.quantityReceived) || 0), 0);
  }

  get totalBilled(): number {
    return this.items.reduce((n, i) => n + (Number(i.quantityBilled) || 0), 0);
  }

  get receivedPct(): number {
    return this.totalOrdered ? Math.min(100, Math.round((this.totalReceived / this.totalOrdered) * 100)) : 0;
  }

  get billedPct(): number {
    return this.totalOrdered ? Math.min(100, Math.round((this.totalBilled / this.totalOrdered) * 100)) : 0;
  }

  // Whole days past the expected delivery date while stock is still due
  // (null once received/cancelled, or when there's no date).
  get daysLate(): number | null {
    if (!this.expectedDeliveryDate) return null;
    if (![purchaseOrderStatus.ORDERED, purchaseOrderStatus.PARTIALLY_RECEIVED].includes(this.status as purchaseOrderStatus)) return null;
    const [y, m, d] = this.expectedDeliveryDate.split('-').map(Number);
    const today = new Date();
    return Math.round((Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()) - Date.UTC(y, m - 1, d)) / 86_400_000);
  }

  // ---- Status stepper / billing toggle ----
  readonly statusFlow = [
    { value: purchaseOrderStatus.DRAFT, label: 'Draft' },
    { value: purchaseOrderStatus.ORDERED, label: 'Ordered' },
    { value: purchaseOrderStatus.PARTIALLY_RECEIVED, label: 'Partially received' },
    { value: purchaseOrderStatus.RECEIVED, label: 'Received' },
  ];

  readonly billingChoices = [
    { value: purchaseOrderBillingStatus.UNBILLED, label: 'Unbilled', dot: 'bg-neutral-400' },
    { value: purchaseOrderBillingStatus.BILLED, label: 'Billed', dot: 'bg-indigo-500' },
    { value: purchaseOrderBillingStatus.OVERDUE, label: 'Overdue', dot: 'bg-rose-500' },
    { value: purchaseOrderBillingStatus.PAID, label: 'Paid', dot: 'bg-emerald-500' },
  ];

  get isCancelled(): boolean {
    return this.status === purchaseOrderStatus.CANCELLED;
  }

  get statusIndex(): number {
    return this.statusFlow.findIndex((s) => s.value === this.status);
  }

  setStatus(value: string): void {
    this.status = value;
    this.onStatusChange(value);
  }

  // Received / Billed are always 0 on a fresh draft, so they only take up
  // room until the PO is saved or goods start arriving.
  get showReceiptColumns(): boolean {
    return (
      this.isEditMode ||
      this.totalReceived > 0 ||
      this.totalBilled > 0 ||
      this.status === purchaseOrderStatus.PARTIALLY_RECEIVED ||
      this.status === purchaseOrderStatus.RECEIVED
    );
  }

  get validLineCount(): number {
    return this.items.filter((i) => i.productName.trim() && i.quantity > 0).length;
  }

  // What still stops the PO being saved, shown in the save bar.
  get saveBlockers(): string[] {
    const blockers: string[] = [];
    if (!this.supplierId) blockers.push('Choose a supplier');
    if (this.validLineCount === 0) blockers.push('Add an item');
    return blockers;
  }

  // "in 12 days" / "today" / "3 days ago" under the expected delivery date.
  get expectedDeliveryHint(): { text: string; warn: boolean } | null {
    if (!this.expectedDeliveryDate) return null;
    if (this.orderDate && this.expectedDeliveryDate < this.orderDate) {
      return { text: 'Before the order date', warn: true };
    }
    const [y, m, d] = this.expectedDeliveryDate.split('-').map(Number);
    const today = new Date();
    const days = Math.round((Date.UTC(y, m - 1, d) - Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())) / 86_400_000);
    if (days === 0) return { text: 'Due today', warn: false };
    const n = Math.abs(days);
    const label = `${n} day${n === 1 ? '' : 's'}`;
    return days > 0 ? { text: `In ${label}`, warn: false } : { text: `${label} ago`, warn: !!this.daysLate };
  }

  // Same as picking "Received", without changing the status by hand first.
  receiveAll(): void {
    this.items.forEach((item) => (item.quantityReceived = item.quantity));
  }

  onSave(): void {
    if (!this.supplierId) {
      this.alertService.alert('Select a supplier', 'error');
      return;
    }
    const validItems = this.items.filter((i) => i.productName.trim() && i.quantity > 0);
    if (this.items.length > 0 && validItems.length === 0) {
      this.alertService.alert('Add at least one item with a quantity', 'error');
      return;
    }

    const input: PurchaseOrderInput = {
      supplierId: this.supplierId,
      buyerName: this.buyerName,
      store: this.store,
      currency: this.currency,
      orderDate: this.orderDate,
      expectedDeliveryDate: this.expectedDeliveryDate || null,
      status: this.status,
      billingStatus: this.billingStatus,
      sourceDocument: this.sourceDocument || undefined,
      incoterms: this.incoterms || undefined,
      paymentTerms: this.paymentTerms || undefined,
      portOfLoading: this.portOfLoading || undefined,
      portOfDischarge: this.portOfDischarge || undefined,
      notes: this.notes || undefined,
      allocationMethod: this.allocationMethod,
      exchangeRate: this.currency === 'GBP' ? 1 : this.exchangeRate,
      charges: this.charges
        .filter((c) => Number(c.amount) > 0)
        .map((c) => ({ chargeType: c.chargeType, description: c.description || null, amount: Number(c.amount) })),
      items: validItems.map((item) => ({
        productId: item.productId,
        productName: item.productName,
        sku: item.sku || undefined,
        quantity: item.quantity,
        quantityReceived: item.quantityReceived,
        quantityBilled: item.quantityBilled,
        unitCost: item.unitCost,
        unitWeight: item.unitWeight,
        taxRate: item.taxRate,
      })),
    };

    this.saving = true;
    const request =
      this.isEditMode && this.purchaseOrderId
        ? this.purchaseOrderService.updatePurchaseOrder(this.purchaseOrderId, input)
        : this.purchaseOrderService.createPurchaseOrder(input);

    request.subscribe({
      next: () => {
        this.saving = false;
        this.alertService.alert(
          this.isEditMode ? 'Purchase order updated' : 'Purchase order created',
          'success',
        );
        this.backToList();
      },
      error: (error) => {
        this.saving = false;
        this.alertService.alert(error?.error?.message || 'Could not save purchase order', 'error');
      },
    });
  }

  getSelectedSupplier(): Supplier | undefined {
    return this.suppliers.find((s) => s.id === this.supplierId);
  }

  getSupplierInitials(supplier: Supplier): string {
    return supplier.name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0])
      .join('')
      .toUpperCase();
  }

  // Adds a supplier in a dialog rather than on the supplier page, so the
  // lines already typed on this PO stay put. The new one is picked straight away.
  onNewSupplier(name = ''): void {
    this.dialog
      .open(QuickSupplierDialogComponent, {
        width: '560px',
        maxWidth: '96vw',
        data: { name: name || this.supplierSearchTerm, suppliers: this.suppliers },
      })
      .afterClosed()
      .subscribe((supplier: Supplier | null | undefined) => {
        if (!supplier?.id) return;
        if (!this.suppliers.some((s) => s.id === supplier.id)) this.suppliers = [...this.suppliers, supplier];
        this.supplierId = supplier.id;
        this.supplierSearchTerm = supplier.name;
      });
  }

  onOpenSupplier(): void {
    if (this.supplierId) this.router.navigate(['/suppliers/edit', this.supplierId]);
  }

  onCancel(): void {
    this.backToList();
  }

  // The list's last filters/page, for the breadcrumb and backToList.
  readonly listQuery = lastListQuery();

  // Back to the list on the same filters/page it was left on.
  private backToList(): void {
    this.router.navigate(['/purchases'], { queryParams: lastListQuery() });
  }

  onPrint(): void {
    if (!this.reference || !this.supplierId) {
      this.alertService.alert('Save the purchase order before printing', 'error');
      return;
    }
    // Generated client-side with pdfmake, same as the Sales invoice
    // download - see PurchaseOrderPdfService for why.
    this.purchaseOrderPdfService.print({
      reference: this.reference,
      orderDate: this.orderDate,
      expectedDeliveryDate: this.expectedDeliveryDate || null,
      paymentTerms: this.paymentTerms,
      incoterms: this.incoterms,
      portOfLoading: this.portOfLoading,
      portOfDischarge: this.portOfDischarge,
      notes: this.notes,
      currency: this.currency,
      supplier: this.getSelectedSupplier(),
      items: this.items.map((item) => ({
        sku: item.sku,
        productName: item.productName,
        quantity: item.quantity,
        unitCost: item.unitCost,
        taxRate: item.taxRate,
      })),
      subtotal: this.getSubtotal(),
      taxTotal: this.getTotalTax(),
      grandTotal: this.getGrandTotal(),
    });
  }

  private getAdjacentPurchaseOrderId(direction: -1 | 1): number | null {
    if (this.purchaseOrderId === null) return null;
    const index = this.allPurchaseOrderIds.indexOf(this.purchaseOrderId);
    if (index === -1) return null;
    return this.allPurchaseOrderIds[index + direction] ?? null;
  }

  get previousPurchaseOrderId(): number | null {
    return this.getAdjacentPurchaseOrderId(-1);
  }

  get nextPurchaseOrderId(): number | null {
    return this.getAdjacentPurchaseOrderId(1);
  }

  goToPreviousPurchaseOrder(): void {
    const id = this.previousPurchaseOrderId;
    if (id) this.router.navigate(['/purchases/edit', id]);
  }

  goToNextPurchaseOrder(): void {
    const id = this.nextPurchaseOrderId;
    if (id) this.router.navigate(['/purchases/edit', id]);
  }

  onNewPurchaseOrder(): void {
    this.router.navigate(['/purchases/create']);
  }

  // TODO: wire up to a real endpoint once one exists - this only
  // simulates sending, it doesn't email anything.
  onSend(): void {
    const supplier = this.getSelectedSupplier();
    if (!supplier) {
      this.alertService.alert('Select a supplier before sending', 'error');
      return;
    }
    if (!supplier.email) {
      this.alertService.alert(`${supplier.name} has no email on file`, 'error');
      return;
    }
    this.alertService.alert(`Purchase order sent to ${supplier.email}`, 'success');
  }
}
